import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from '@/locales/en/common.json';
import enNavigation from '@/locales/en/navigation.json';
import enWorkspace from '@/locales/en/workspace.json';
import enPages from '@/locales/en/pages.json';
import enUi from '@/locales/en/ui.json';
import enAuth from '@/locales/en/auth.json';
import enPublicJobBoard from '@/locales/en/public-job-board.json';
import enInterview from '@/locales/en/interview.json';
import enToast from '@/locales/en/toast.json';
import enRecruiter from '@/locales/en/recruiter.json';

import esCommon from '@/locales/es/common.json';
import esNavigation from '@/locales/es/navigation.json';
import esWorkspace from '@/locales/es/workspace.json';
import esPages from '@/locales/es/pages.json';
import esUi from '@/locales/es/ui.json';
import esAuth from '@/locales/es/auth.json';
import esPublicJobBoard from '@/locales/es/public-job-board.json';
import esInterview from '@/locales/es/interview.json';
import esToast from '@/locales/es/toast.json';
import esRecruiter from '@/locales/es/recruiter.json';

import zhCommon from '@/locales/zh/common.json';
import zhNavigation from '@/locales/zh/navigation.json';
import zhWorkspace from '@/locales/zh/workspace.json';
import zhPages from '@/locales/zh/pages.json';
import zhUi from '@/locales/zh/ui.json';
import zhAuth from '@/locales/zh/auth.json';
import zhPublicJobBoard from '@/locales/zh/public-job-board.json';
import zhInterview from '@/locales/zh/interview.json';
import zhToast from '@/locales/zh/toast.json';
import zhRecruiter from '@/locales/zh/recruiter.json';

import frCommon from '@/locales/fr/common.json';
import frNavigation from '@/locales/fr/navigation.json';
import frWorkspace from '@/locales/fr/workspace.json';
import frPages from '@/locales/fr/pages.json';
import frUi from '@/locales/fr/ui.json';
import frAuth from '@/locales/fr/auth.json';
import frPublicJobBoard from '@/locales/fr/public-job-board.json';
import frInterview from '@/locales/fr/interview.json';
import frToast from '@/locales/fr/toast.json';
import frRecruiter from '@/locales/fr/recruiter.json';

import jaCommon from '@/locales/ja/common.json';
import jaNavigation from '@/locales/ja/navigation.json';
import jaWorkspace from '@/locales/ja/workspace.json';
import jaPages from '@/locales/ja/pages.json';
import jaUi from '@/locales/ja/ui.json';
import jaAuth from '@/locales/ja/auth.json';
import jaPublicJobBoard from '@/locales/ja/public-job-board.json';
import jaInterview from '@/locales/ja/interview.json';
import jaToast from '@/locales/ja/toast.json';
import jaRecruiter from '@/locales/ja/recruiter.json';

const resources = {
  en: {
    common: enCommon,
    navigation: enNavigation,
    workspace: enWorkspace,
    pages: enPages,
    ui: enUi,
    auth: enAuth,
    'public-job-board': enPublicJobBoard,
    interview: enInterview,
    toast: enToast,
    recruiter: enRecruiter,
  },
  es: {
    common: esCommon,
    navigation: esNavigation,
    workspace: esWorkspace,
    pages: esPages,
    ui: esUi,
    auth: esAuth,
    'public-job-board': esPublicJobBoard,
    interview: esInterview,
    toast: esToast,
    recruiter: esRecruiter,
  },
  zh: {
    common: zhCommon,
    navigation: zhNavigation,
    workspace: zhWorkspace,
    pages: zhPages,
    ui: zhUi,
    auth: zhAuth,
    'public-job-board': zhPublicJobBoard,
    interview: zhInterview,
    toast: zhToast,
    recruiter: zhRecruiter,
  },
  fr: {
    common: frCommon,
    navigation: frNavigation,
    workspace: frWorkspace,
    pages: frPages,
    ui: frUi,
    auth: frAuth,
    'public-job-board': frPublicJobBoard,
    interview: frInterview,
    toast: frToast,
    recruiter: frRecruiter,
  },
  ja: {
    common: jaCommon,
    navigation: jaNavigation,
    workspace: jaWorkspace,
    pages: jaPages,
    ui: jaUi,
    auth: jaAuth,
    'public-job-board': jaPublicJobBoard,
    interview: jaInterview,
    toast: jaToast,
    recruiter: jaRecruiter,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'common',
    supportedLngs: ['en', 'es', 'zh', 'fr', 'ja'],
    
    detection: {
      order: [
        'querystring',       // Allow URL override (?lng=es)
        'localStorage',      // Respect user's explicit choice
        'navigator',         // Auto-detect from browser settings
        'htmlTag',          // Fallback to HTML lang attribute
      ],
      caches: ['localStorage'],
      lookupLocalStorage: 'language', // Match LanguageProvider storage key
      lookupQuerystring: 'lng',
      
      // Language code conversions for regional variants
      convertDetectedLanguage: (lng: string) => {
        // Map regional codes to supported languages
        const languageMap: Record<string, string> = {
          'en-US': 'en',
          'en-GB': 'en',
          'en-AU': 'en',
          'en-CA': 'en',
          'es-ES': 'es',
          'es-MX': 'es',
          'es-AR': 'es',
          'zh-CN': 'zh',
          'zh-TW': 'zh',
          'zh-HK': 'zh',
          'fr-FR': 'fr',
          'fr-CA': 'fr',
          'ja-JP': 'ja',
        };
        
        // Return mapped language or extract base language code
        return languageMap[lng] || lng.split('-')[0];
      },
    },

    interpolation: {
      escapeValue: false,
    },

    react: {
      useSuspense: false,
    },
  });

export default i18n;